/**
 * Flight Editor Validation Utilities
 * Validates flight data for the modal editor
 */

/**
 * Validate airport code (3 letters, all caps)
 */
export const validateAirport = (airport) => {
  if (!airport) return { valid: false, error: 'Airport required' };
  if (!/^[A-Z]{3}$/.test(airport)) {
    return { valid: false, error: 'Airport must be 3 capital letters (e.g., JFK, MIA)' };
  }
  return { valid: true };
};

/**
 * Validate flight number (1-4 alphanumerics, airline code + number)
 */
export const validateFlightNumber = (flightNumber) => {
  if (!flightNumber) return { valid: false, error: 'Flight number required' };
  // Allow formats like: DL123, UA1234, AA99, B6100, etc.
  if (!/^[A-Z0-9]{2,4}[0-9]{1,4}$/.test(flightNumber.toUpperCase())) {
    return { valid: false, error: 'Invalid flight number format (e.g., DL123, UA1234)' };
  }
  return { valid: true };
};

/**
 * Validate time format (HH:MM in 24-hour format)
 */
export const validateTime = (time) => {
  if (!time) return { valid: false, error: 'Time required' };
  if (!/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(time)) {
    return { valid: false, error: 'Invalid time format (must be HH:MM, 00:00-23:59)' };
  }
  return { valid: true };
};

/**
 * Validate that arrival time is after departure time
 * Handles same-day and next-day scenarios
 */
export const validateTimeSequence = (outTime, inTime) => {
  const [outHour, outMin] = outTime.split(':').map(Number);
  const [inHour, inMin] = inTime.split(':').map(Number);
  
  const outMinutes = outHour * 60 + outMin;
  const inMinutes = inHour * 60 + inMin;
  
  // Allow same-day or next-day (if arrival time is "earlier" it's next day)
  const isValid = inMinutes > outMinutes || inMinutes < outMinutes;
  
  if (!isValid) {
    return { valid: false, error: 'Arrival time must be after departure time' };
  }
  
  return { valid: true };
};

/**
 * Calculate block time between two times
 * Returns time string (HH:MM) and total minutes
 */
export const calculateBlockTime = (outTime, inTime) => {
  const [outHour, outMin] = outTime.split(':').map(Number);
  const [inHour, inMin] = inTime.split(':').map(Number);
  
  let outMinutes = outHour * 60 + outMin;
  let inMinutes = inHour * 60 + inMin;
  
  // If arrival is earlier, assume next day
  if (inMinutes <= outMinutes) {
    inMinutes += 24 * 60;
  }
  
  const totalMinutes = inMinutes - outMinutes;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  return {
    timeString: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
    minutes: totalMinutes
  };
};

/**
 * Calculate connection time between two flights
 * Flight 1 arrival to Flight 2 departure
 */
export const calculateConnectionTime = (flight1In, flight2Out) => {
  const [in1Hour, in1Min] = flight1In.split(':').map(Number);
  const [out2Hour, out2Min] = flight2Out.split(':').map(Number);
  
  let in1Minutes = in1Hour * 60 + in1Min;
  let out2Minutes = out2Hour * 60 + out2Min;
  
  // If departure is earlier or same as arrival, it's next day
  if (out2Minutes <= in1Minutes) {
    out2Minutes += 24 * 60;
  }
  
  const connectionMinutes = out2Minutes - in1Minutes;
  const hours = Math.floor(connectionMinutes / 60);
  const minutes = connectionMinutes % 60;
  
  return {
    timeString: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
    minutes: connectionMinutes,
    isShort: connectionMinutes < 45,
    isTight: connectionMinutes < 30
  };
};

/**
 * Validate entire flight object (Manual Mode)
 */
export const validateFlight = (flight) => {
  const errors = {};
  
  // Destination airport
  const destValidation = validateAirport(flight.destination);
  if (!destValidation.valid) errors.destination = destValidation.error;
  
  // Flight number
  const fnValidation = validateFlightNumber(flight.flightNumber);
  if (!fnValidation.valid) errors.flightNumber = fnValidation.error;
  
  // Departure time
  const outValidation = validateTime(flight.scheduledOut);
  if (!outValidation.valid) errors.scheduledOut = outValidation.error;
  
  // Arrival time
  const inValidation = validateTime(flight.scheduledIn);
  if (!inValidation.valid) errors.scheduledIn = inValidation.error;
  
  // Time sequence (only if both times valid)
  if (!errors.scheduledOut && !errors.scheduledIn) {
    const seqValidation = validateTimeSequence(flight.scheduledOut, flight.scheduledIn);
    if (!seqValidation.valid) errors.timeSequence = seqValidation.error;
  }
  
  // Check for required fields
  if (!flight.origin) errors.origin = 'Origin airport required';
  if (!flight.destination) errors.destination = 'Destination airport required';
  if (!flight.flightNumber) errors.flightNumber = 'Flight number required';
  if (!flight.scheduledOut) errors.scheduledOut = 'Departure time required';
  if (!flight.scheduledIn) errors.scheduledIn = 'Arrival time required';
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Get field-specific CSS classes for validation
 */
export const getFieldClassName = (fieldName, errors) => {
  if (!errors || !errors[fieldName]) {
    return 'border-gray-300 focus:border-blue-500';
  }
  return 'border-red-500 focus:border-red-500 bg-red-50';
};

/**
 * Check if connection time is safe (minimum 45 minutes)
 */
export const hasConnectionWarning = (flight1In, flight2Out) => {
  const connection = calculateConnectionTime(flight1In, flight2Out);
  return {
    isWarning: connection.isShort,
    message: connection.isShort 
      ? `⚠️ Short connection: ${connection.timeString} (minimum 45 min recommended)`
      : null
  };
};
